import * as React from 'react';
import { authFetchJSON } from '../lib/authFetch';
import { supabase } from '../lib/supabase';
import { hasWorkoutManagerAccess, normalizePlan } from '../lib/planAccess';

type Profile = {
  plan: 'free' | 'pro' | 'coach';
  subscriptionStatus?: string | null;
  equipment?: string[] | null;
  friendId?: string | null;
  username?: string | null;
  profilePictureDataUrl?: string | null;
  profilePictureName?: string | null;
  workoutManagerUnlocked?: boolean | null;
  plannerWeeklyLimit?: number | null;
  plannerWeeklyUsage?: number | null;
  plannerWeeklyRemaining?: number | null;
} | null;

function normalizeEquipment(values: unknown): string[] {
  const raw = Array.isArray(values) ? values : [];
  const normed = raw
    .map(v => String(v ?? '').trim().toLowerCase())
    .filter(Boolean)
    .filter(v => v !== 'machine'); // drop overly generic tag
  // Always keep bodyweight
  return Array.from(new Set([...normed, 'bodyweight']));
}

const Ctx = React.createContext<{
  profile: Profile;
  refresh: () => Promise<'free' | 'pro' | 'coach'>;
  updateEquipment: (list: string[]) => Promise<{ ok: boolean; equipment: string[] }>;
  updateUsername: (name: string) => Promise<{ ok: boolean; username: string | null }>;
  updateProfilePicture: (
    dataUrl: string | null,
    name: string | null
  ) => Promise<{ ok: boolean; profilePictureDataUrl: string | null; profilePictureName: string | null }>;
  updateWorkoutManagerUnlocked: (unlocked: boolean) => Promise<{ ok: boolean; unlocked: boolean }>;
}>({
  profile: null,
  refresh: async () => 'free' as 'free' | 'pro' | 'coach',
  updateEquipment: async () => ({ ok: false, equipment: [] }),
  updateUsername: async () => ({ ok: false, username: null }),
  updateProfilePicture: async () => ({ ok: false, profilePictureDataUrl: null, profilePictureName: null }),
  updateWorkoutManagerUnlocked: async () => ({ ok: false, unlocked: false }),
});

export function ProfileProvider({ children }: { children: React.ReactNode }) {
  const [profile, setProfile] = React.useState<Profile>(null);
  const [userId, setUserId] = React.useState<string | null>(null);

  // Track current user id (for local fallback)
  React.useEffect(() => {
    let cancelled = false;
    supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setUserId(data.session?.user?.id ?? null);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setUserId(session?.user?.id ?? null);
    });
    return () => {
      cancelled = true;
      sub?.subscription?.unsubscribe();
    };
  }, []);

  const loadLocalEquipment = React.useCallback((): string[] => {
    if (!userId) return [];
    try {
      const raw = localStorage.getItem(`equipment:${userId}`);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return normalizeEquipment(parsed);
    } catch {
      return [];
    }
  }, [userId]);

  const saveLocalEquipment = React.useCallback(
    (list: string[]) => {
      if (!userId) return;
      try {
        localStorage.setItem(`equipment:${userId}`, JSON.stringify(normalizeEquipment(list)));
      } catch {
        // ignore
      }
    },
    [userId]
  );

  const loadLocalWorkoutManager = React.useCallback((): boolean => {
    if (!userId) return false;
    try {
      return localStorage.getItem(`workoutManagerUnlocked:${userId}`) === 'true';
    } catch {
      return false;
    }
  }, [userId]);

  const saveLocalWorkoutManager = React.useCallback(
    (unlocked: boolean) => {
      if (!userId) return;
      try {
        localStorage.setItem(`workoutManagerUnlocked:${userId}`, unlocked ? 'true' : 'false');
      } catch {
        // ignore
      }
    },
    [userId]
  );

  const persistWorkoutManagerUnlocked = React.useCallback(
    async (unlocked: boolean): Promise<{ ok: boolean; unlocked: boolean }> => {
      const res = await authFetchJSON<{ workoutManagerUnlocked?: boolean }>(
        `${import.meta.env.VITE_API_BASE}/api/profile/workout-manager-unlocked`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ unlocked: !!unlocked }),
        }
      );

      if (res.ok) {
        const next = !!res.data?.workoutManagerUnlocked;
        saveLocalWorkoutManager(next);
        setProfile((prev) => ({
          ...(prev ?? {
            plan: 'free',
            subscriptionStatus: null,
            equipment: normalizeEquipment([]),
            friendId: null,
            username: null,
            profilePictureDataUrl: null,
            profilePictureName: null,
            plannerWeeklyLimit: 1,
            plannerWeeklyUsage: 0,
            plannerWeeklyRemaining: 1,
          }),
          workoutManagerUnlocked: next,
        }));
        return { ok: true, unlocked: next };
      }

      return { ok: false, unlocked: !!unlocked };
    },
    [saveLocalWorkoutManager]
  );

  const load = React.useCallback(async () => {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    const res = await authFetchJSON<{
      plan: 'free' | 'pro' | 'coach';
      subscriptionStatus?: string;
      equipment?: string[];
      friendId?: string | null;
      username?: string | null;
      profilePictureDataUrl?: string | null;
      profilePictureName?: string | null;
      workoutManagerUnlocked?: boolean;
      plannerWeeklyLimit?: number | null;
      plannerWeeklyUsage?: number | null;
      plannerWeeklyRemaining?: number | null;
    }>(
      `${import.meta.env.VITE_API_BASE}/api/profile?tz=${encodeURIComponent(tz)}`
    );
    if (res.ok) {
      const plan = normalizePlan(res.data?.plan);
      const eqApi = normalizeEquipment(res.data?.equipment);
      const eq = eqApi.length ? eqApi : loadLocalEquipment();
      const localWorkoutManagerUnlocked = loadLocalWorkoutManager();
      const apiWorkoutManagerUnlocked =
        typeof res.data?.workoutManagerUnlocked === 'boolean'
          ? res.data.workoutManagerUnlocked
          : null;
      const shouldPromoteLocalUnlock =
        localWorkoutManagerUnlocked && apiWorkoutManagerUnlocked === false;
      const workoutManagerUnlocked = hasWorkoutManagerAccess(
        plan,
        shouldPromoteLocalUnlock ? true : (apiWorkoutManagerUnlocked ?? localWorkoutManagerUnlocked)
      );
      if (typeof apiWorkoutManagerUnlocked === 'boolean' && !shouldPromoteLocalUnlock) {
        saveLocalWorkoutManager(apiWorkoutManagerUnlocked);
      }
      setProfile({
        plan,
        subscriptionStatus: res.data?.subscriptionStatus ?? null,
        equipment: eq,
        friendId: res.data?.friendId ?? null,
        username: res.data?.username ?? null,
        profilePictureDataUrl: res.data?.profilePictureDataUrl ?? null,
        profilePictureName: res.data?.profilePictureName ?? null,
        workoutManagerUnlocked,
        plannerWeeklyLimit:
          typeof res.data?.plannerWeeklyLimit === 'number' ? res.data.plannerWeeklyLimit : null,
        plannerWeeklyUsage:
          typeof res.data?.plannerWeeklyUsage === 'number' ? res.data.plannerWeeklyUsage : 0,
        plannerWeeklyRemaining:
          typeof res.data?.plannerWeeklyRemaining === 'number'
            ? res.data.plannerWeeklyRemaining
            : null,
      });
      return plan;
    } else {
      setProfile({
        plan: 'free',
        subscriptionStatus: null,
        equipment: normalizeEquipment(loadLocalEquipment()),
        friendId: null,
        username: null,
        profilePictureDataUrl: null,
        profilePictureName: null,
        workoutManagerUnlocked: loadLocalWorkoutManager(),
        plannerWeeklyLimit: 1,
        plannerWeeklyUsage: 0,
        plannerWeeklyRemaining: 1,
      });
      return 'free' as const;
    }
  }, [
    loadLocalEquipment,
    loadLocalWorkoutManager,
    saveLocalWorkoutManager,
  ]);

  const updateEquipment = React.useCallback(
    async (list: string[]): Promise<{ ok: boolean; equipment: string[] }> => {
      const cleaned = normalizeEquipment(list);
      const res = await authFetchJSON<{ equipment?: unknown }>(
        `${import.meta.env.VITE_API_BASE}/api/profile/equipment`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ equipment: cleaned }),
        }
      );

      if (res.ok) {
        const eq = normalizeEquipment((res.data as any)?.equipment ?? cleaned);
        saveLocalEquipment(eq);
        setProfile((prev) => ({
          ...(prev ?? {
            plan: 'free',
            subscriptionStatus: null,
            profilePictureDataUrl: null,
            profilePictureName: null,
            plannerWeeklyLimit: 1,
            plannerWeeklyUsage: 0,
            plannerWeeklyRemaining: 1,
          }),
          equipment: eq,
          friendId: prev?.friendId ?? null,
          username: prev?.username ?? null,
        }));
        return { ok: true, equipment: eq };
      }

      saveLocalEquipment(cleaned);
      return { ok: false, equipment: profile?.equipment ?? cleaned };
    },
    [profile?.equipment, saveLocalEquipment]
  );

  const updateUsername = React.useCallback(
    async (name: string): Promise<{ ok: boolean; username: string | null }> => {
      const res = await authFetchJSON<{ username?: string | null }>(
        `${import.meta.env.VITE_API_BASE}/api/profile/username`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: name }),
        }
      );

      if (res.ok) {
        const username = res.data?.username ?? null;
        setProfile((prev) => ({
          ...(prev ?? {
            plan: 'free',
            subscriptionStatus: null,
            equipment: normalizeEquipment([]),
            friendId: null,
            profilePictureDataUrl: null,
            profilePictureName: null,
            plannerWeeklyLimit: 1,
            plannerWeeklyUsage: 0,
            plannerWeeklyRemaining: 1,
          }),
          username,
        }));
        return { ok: true, username };
      }

      return { ok: false, username: profile?.username ?? null };
    },
    [profile?.username]
  );

  const updateProfilePicture = React.useCallback(
    async (
      dataUrl: string | null,
      name: string | null
    ): Promise<{ ok: boolean; profilePictureDataUrl: string | null; profilePictureName: string | null }> => {
      const res = await authFetchJSON<{
        profilePictureDataUrl?: string | null;
        profilePictureName?: string | null;
      }>(
        `${import.meta.env.VITE_API_BASE}/api/profile/picture`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            profilePictureDataUrl: dataUrl,
            profilePictureName: name,
          }),
        }
      );

      if (res.ok) {
        const profilePictureDataUrl = res.data?.profilePictureDataUrl ?? null;
        const profilePictureName = res.data?.profilePictureName ?? null;
        setProfile((prev) => ({
          ...(prev ?? {
            plan: 'free',
            subscriptionStatus: null,
            equipment: normalizeEquipment([]),
            friendId: null,
            username: null,
            plannerWeeklyLimit: 1,
            plannerWeeklyUsage: 0,
            plannerWeeklyRemaining: 1,
          }),
          profilePictureDataUrl,
          profilePictureName,
        }));
        return { ok: true, profilePictureDataUrl, profilePictureName };
      }

      return {
        ok: false,
        profilePictureDataUrl: profile?.profilePictureDataUrl ?? null,
        profilePictureName: profile?.profilePictureName ?? null,
      };
    },
    [profile?.profilePictureDataUrl, profile?.profilePictureName]
  );

  const updateWorkoutManagerUnlocked = React.useCallback(
    async (unlocked: boolean): Promise<{ ok: boolean; unlocked: boolean }> => {
      return persistWorkoutManagerUnlocked(unlocked);
    },
    [persistWorkoutManagerUnlocked]
  );

  React.useEffect(() => {
    load();
    const onAuth = () => load();
    window.addEventListener('auth:changed', onAuth);
    window.addEventListener('auth:unauthorized', onAuth);
    return () => {
      window.removeEventListener('auth:changed', onAuth);
      window.removeEventListener('auth:unauthorized', onAuth);
    };
  }, [load]);

  return (
    <Ctx.Provider
      value={{
        profile,
        refresh: load,
        updateEquipment,
        updateUsername,
        updateProfilePicture,
        updateWorkoutManagerUnlocked,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useProfile() {
  return React.useContext(Ctx);
}
